import {SignatureDataEntry} from '../../shared/files/files.model';
import {SignatureRoleEntry} from '../template-management.interfaces';

export const SIGNATURE_ASSETS: string[] = [
  'assets/images/signatures/signature1.png',
  'assets/images/signatures/signature2.png',
  'assets/images/signatures/signature3.png',
  'assets/images/signatures/signature4.png',
  'assets/images/signatures/signature5.png',
  'assets/images/signatures/signature6.png'
];

export const MAX_SIGNATURE_ROLES = SIGNATURE_ASSETS.length;

export const MAX_SIGNATURE_ROLE_LENGTH = 25;

const FINGERPRINT_LENGTH = 512;
const SIGNATURE_ICON = '\uf5b7';
const SIGNATURE_ICON_FONT_FAMILY = 'Font Awesome 6 Pro';
const SIGNATURE_CONTENT_LEFT_PADDING = 14;
const SIGNATURE_CONTENT_RIGHT_PADDING = 10;
const SIGNATURE_CONTENT_VERTICAL_PADDING = 4;
const SIGNATURE_ICON_GAP = 8;

export function base64Of(dataUri: string): string {
  return dataUri?.includes(',') ? dataUri.split(',')[1] : (dataUri ?? '');
}

export function signatureImageNameOf(signatureIndex: number): string {
  return fileNameOf(SIGNATURE_ASSETS[signatureIndex] ?? SIGNATURE_ASSETS[0]);
}

export function signatureIndexOfImageName(signatureImage: string): number {
  return signatureImage
    ? SIGNATURE_ASSETS.findIndex((asset) => fileNameOf(asset) === signatureImage)
    : -1;
}

function fileNameOf(asset: string): string {
  return asset.substring(asset.lastIndexOf('/') + 1);
}

export function fingerprintOfBase64(base64: string): string {
  if (!base64) {
    return '';
  }
  try {
    const binary = atob(base64);
    return btoa(binary.substring(0, FINGERPRINT_LENGTH));
  } catch {
    return '';
  }
}

export function fingerprintOfDataUri(dataUri: string): string {
  return fingerprintOfBase64(base64Of(dataUri));
}

export function encodeSignatureData(entries: SignatureRoleEntry[]): SignatureDataEntry[] {
  return (entries ?? []).reduce((data: SignatureDataEntry[], entry: SignatureRoleEntry, index: number) =>
    data.concat((entry.fingerprints ?? []).map((imageFingerprint) => ({
      role: entry.role,
      imageFingerprint,
      signatureMethods: [...(entry.signatureMethods ?? [])],
      signingOrder: index,
      signatureImage: signatureImageNameOf(entry.signatureIndex)
    }))), []);
}

export function decodeSignatureData(entries: SignatureDataEntry[]): SignatureRoleEntry[] {
  const entriesByRole = new Map<string, SignatureRoleEntry>();
  const signingOrderByRole = new Map<string, number>();
  (entries ?? []).forEach((entry) => {
    if (!entry?.role) {
      return;
    }
    const known = entriesByRole.get(entry.role) ?? {
      role: entry.role,
      signatureIndex: signatureIndexOfImageName(entry.signatureImage),
      imageDataUri: '',
      fingerprints: [],
      signatureMethods: [...(entry.signatureMethods ?? [])]
    };
    if (entry.imageFingerprint && !known.fingerprints.includes(entry.imageFingerprint)) {
      known.fingerprints = [...known.fingerprints, entry.imageFingerprint];
    }
    entriesByRole.set(entry.role, known);
    if (!signingOrderByRole.has(entry.role)) {
      signingOrderByRole.set(entry.role, entry.signingOrder ?? 0);
    }
  });
  return Array.from(entriesByRole.values())
    .sort((left, right) => signingOrderByRole.get(left.role) - signingOrderByRole.get(right.role));
}

export function stampRoleOnSignature(imageSrc: string, role: string): Promise<string> {
  return loadSignatureIconFont().then(() => stampRoleOnSignatureImage(imageSrc, role, 'LEFT_ALIGNED'));
}

function stampLegacyRoleOnSignature(imageSrc: string, role: string): Promise<string> {
  return stampRoleOnSignatureImage(imageSrc, role, 'TEXT_ONLY');
}

function stampPreviouslyCenteredRoleOnSignature(imageSrc: string, role: string): Promise<string> {
  return loadSignatureIconFont().then(() => stampRoleOnSignatureImage(imageSrc, role, 'CENTERED'));
}

function stampRoleOnSignatureImage(imageSrc: string,
                                   role: string,
                                   layout: 'LEFT_ALIGNED' | 'CENTERED' | 'TEXT_ONLY'): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = (): void => {
      try {
        resolve(drawRoleOnImage(image, role, layout));
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = reject;
    image.src = imageSrc;
  });
}

function loadSignatureIconFont(): Promise<void> {
  if (!document.fonts) {
    return Promise.resolve();
  }
  return document.fonts.load(`300 16px "${SIGNATURE_ICON_FONT_FAMILY}"`, SIGNATURE_ICON)
    .then(() => undefined)
    .catch(() => undefined);
}

/**
 * The signature images inserted in the document are the only durable link between a signer role and
 * the asset it was created from, so the asset is resolved by re-stamping every candidate and
 * matching its fingerprint against the ones stored on the role.
 */
export function resolveSignatureIndexByFingerprints(role: string,
                                                    fingerprints: string[],
                                                    fallbackIndex = 0): Promise<number> {
  const knownFingerprints = new Set((fingerprints ?? []).filter(Boolean));
  if (!knownFingerprints.size) {
    return Promise.resolve(fallbackIndex);
  }
  return Promise.all(SIGNATURE_ASSETS.map((asset) => Promise.all([
    stampRoleOnSignature(asset, role),
    stampPreviouslyCenteredRoleOnSignature(asset, role),
    stampLegacyRoleOnSignature(asset, role)
  ]).then((dataUris: string[]) => dataUris.map(fingerprintOfDataUri))
    .catch(() => [])))
    .then((candidates: string[][]) => {
      const matchedIndex = candidates.findIndex((fingerprints) =>
        fingerprints.some((fingerprint) => !!fingerprint && knownFingerprints.has(fingerprint)));
      return matchedIndex >= 0 ? matchedIndex : fallbackIndex;
    });
}

function drawRoleOnImage(image: HTMLImageElement,
                         role: string,
                         layout: 'LEFT_ALIGNED' | 'CENTERED' | 'TEXT_ONLY'): string {
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const fontSize = Math.max(10, Math.round(canvas.height / 6));
  context.textBaseline = 'middle';
  context.fillStyle = '#1a1a1a';
  const includeIcon = layout !== 'TEXT_ONLY';
  const maxContentWidth = layout === 'LEFT_ALIGNED'
    ? canvas.width - SIGNATURE_CONTENT_LEFT_PADDING - SIGNATURE_CONTENT_RIGHT_PADDING
    : canvas.width * 0.9;
  const iconFontSize = fontSize * 2;
  const iconGap = includeIcon
    ? layout === 'LEFT_ALIGNED' ? SIGNATURE_ICON_GAP : fontSize * 0.4
    : 0;
  context.font = `bold ${fontSize}px Arial, sans-serif`;
  const roleWidth = Math.min(context.measureText(role).width, maxContentWidth);
  context.font = `300 ${iconFontSize}px "${SIGNATURE_ICON_FONT_FAMILY}"`;
  const iconWidth = includeIcon ? context.measureText(SIGNATURE_ICON).width : 0;
  const roleMaxWidth = Math.max(0, maxContentWidth - iconWidth - iconGap);
  const contentWidth = Math.min(roleWidth, roleMaxWidth) + iconWidth + iconGap;
  let contentX = layout === 'LEFT_ALIGNED'
    ? SIGNATURE_CONTENT_LEFT_PADDING
    : (canvas.width - contentWidth) / 2;
  const contentY = SIGNATURE_CONTENT_VERTICAL_PADDING
    + (canvas.height - SIGNATURE_CONTENT_VERTICAL_PADDING * 2) / 2;

  if (includeIcon) {
    context.textAlign = 'left';
    context.font = `300 ${iconFontSize}px "${SIGNATURE_ICON_FONT_FAMILY}"`;
    context.fillText(SIGNATURE_ICON, contentX, contentY);
    contentX += iconWidth + iconGap;
  }
  context.font = `bold ${fontSize}px Arial, sans-serif`;
  context.textAlign = 'left';
  context.fillText(role, contentX, contentY, roleMaxWidth);

  return canvas.toDataURL('image/png');
}
